import { Popover } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ShippingOption } from '@medusajs/medusa';
import { AxiosResponse } from 'axios';
import classNames from 'classnames';
import moment from 'moment';
import Image from 'next/image';
import { polishPlurals } from 'polish-plurals';
import { useEffect, useMemo, useRef, useState } from 'react';

import ProductAccordion from '@/components/ProductAccordion';
import ProductTextShowMore from '@/components/ProductTextShowMore';
import { Store } from '@/domain/store';
import { medusaClient } from '@/services/apiClient';
import { stateInfo } from '@/utils/const';
import { dateContent } from '@/utils/date';
import { formatPrice } from '@/utils/getPrice';
import guaranteeIcon from 'public/icons/certifyIcon.svg';
import dateIcon from 'public/icons/dateIcon.svg';
import infoIcon from 'public/icons/info.svg';
import labelIcon from 'public/icons/labelIcon.svg';
import locationIcon from 'public/icons/locationIcon.svg';

import { Product } from '../../types/product';
import { ProductAttributes } from '../ProductAttributes/ProductAttributes';

import styles from './styles.module.scss';

interface ProductDescriptionProps {
  isGalleryOpen: boolean;
  store: Store;
  shippingOptions?: ShippingOption[];
  product: Product;
  isMobile: boolean;
  className?: string;
}

/**
 * Widget product details component that is combining product attributes and product description
 * @param props
 * @returns
 */
export const ProductDetails = (props: ProductDescriptionProps) => {
  const productDetailsFinishRef = useRef<HTMLDivElement>(null);
  const productDetailsLastRef = useRef<HTMLDivElement>(null);
  const { isGalleryOpen, store, shippingOptions, isMobile, product } = props;

  const [storeOrdersAmount, setStoreOrdersAmount] = useState(0);

  useEffect(() => {
    const sellerId = store.id;

    const fetchStoreOrders = async (sellerId: string) => {
      try {
        const response: AxiosResponse<any> = await medusaClient(
          `/store/orders/count/${sellerId}`,
        );

        if (response.status === 200) {
          setStoreOrdersAmount(response.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchStoreOrders(sellerId as string);
  }, [store.id]);

  const { description } = product;

  const [stateInfoOpened, { close: closeStateInfo, open: openStateInfo }] =
    useDisclosure(false);

  const sellerInfo = useMemo(() => {
    const date = moment(store.created_at).startOf('day').fromNow(true);

    const info = [
      {
        id: 's1',
        icon: guaranteeIcon,
        label: 'Zweryfikowany sprzedawca',
      },
      {
        ...(store.city && {
          id: 's2',
          icon: locationIcon,
          label: store.city,
        }),
      },
      {
        ...(storeOrdersAmount !== 0 && {
          id: 's3',
          icon: labelIcon,
          label: `${storeOrdersAmount} ${polishPlurals(
            'sprzedany produkt',
            'sprzedane produkty',
            'sprzedanych produktów',
            storeOrdersAmount,
          )}`,
        }),
      },
      {
        id: 's4',
        icon: dateIcon,
        label: date ? `Od ${dateContent(date)} z Patyną` : 'Brak informacji',
      },
    ];

    return {
      info,
    };
  }, [store.city, store.created_at, storeOrdersAmount]);
  const productDetailsCloseHeight = productDetailsLastRef?.current
    ? productDetailsLastRef.current.offsetTop +
      productDetailsLastRef.current.offsetHeight
    : undefined;
  const productDetailsOpenHeight = productDetailsFinishRef?.current
    ? productDetailsFinishRef.current.offsetTop +
      productDetailsFinishRef.current.offsetHeight
    : undefined;
  const state = product.metadata.attributes['Stan'];

  return (
    <div className={classNames('productAccordionInformation', props.className)}>
      <h3 className={styles.productDetailsTitle}>Opis produktu</h3>
      <ProductTextShowMore lines={8}>{description}</ProductTextShowMore>
      <div className={styles.divider} />
      <h3 className={styles.productDetailsTitle}>Szczegóły produktu</h3>
      {state && (
        <div style={{ position: 'relative' }}>
          <Popover
            width={'32rem'}
            position={isMobile ? 'bottom-start' : 'right'}
            withArrow
            shadow="md"
            opened={stateInfoOpened}
          >
            <div className={styles.stateInfoWrapper}>
              <span>
                <b>Stan:</b> {state}
              </span>
              <Popover.Target>
                <Image
                  onMouseEnter={openStateInfo}
                  onMouseLeave={closeStateInfo}
                  src={infoIcon}
                  alt="Info icon"
                />
              </Popover.Target>
              <Popover.Dropdown
                sx={{ pointerEvents: 'none' }}
                className={styles.stateInfoDropdown}
              >
                {Object.entries(stateInfo).map(([keyName, value]) => (
                  <div key={keyName} className={styles.stateInfoRow}>
                    <b>{keyName} - </b> {value}
                  </div>
                ))}
              </Popover.Dropdown>
            </div>
          </Popover>
        </div>
      )}
      <ProductTextShowMore
        closeHeight={productDetailsCloseHeight}
        openHeight={productDetailsOpenHeight}
      >
        <ProductAttributes
          product={product}
          lastRef={productDetailsLastRef}
          finishRef={productDetailsFinishRef}
          lines={8}
        />
      </ProductTextShowMore>
      <ProductAccordion
        descriptionLabel="O sprzedawcy"
        descriptionText={store.description}
        dataName={store.name}
        dataHandle={store.handle}
        dataInfo={sellerInfo}
        isModalOpened={isGalleryOpen}
      />
      <ProductAccordion
        descriptionLabel="Dostawa i zwroty"
        descriptionText={
          <div className={styles.deliveryAndReturnContainer}>
            <div>
              <p className={styles.deliveryHeadline}>Dostępne formy dostawy:</p>
              <ul className={styles.deliveryOptions}>
                {shippingOptions?.map(so => {
                  const name =
                    so.name === 'Odbiór osobisty' && store.city
                      ? `${so.name} - ${store.city}`
                      : so.name;

                  return (
                    <li key={so.id} className={styles.deliveryitem}>
                      {name}{' '}
                      <span className={styles.bold}>
                        {formatPrice(so.amount ?? 0, 'PLN')}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className={styles.returnOptions}>
              <p className={styles.returnHeadline}>Zwroty:</p>
              <p className={styles.returnText}>
                Jeśli z jakiegokolwiek powodu chcesz zwrócić zamówiony
                przedmiot, masz na to{' '}
                <strong>14 dni od momentu otrzymania przesyłki.</strong>
              </p>
            </div>
          </div>
        }
        isModalOpened={isGalleryOpen}
      />
    </div>
  );
};
